/** Знания — SPEC-2 §4; Spec 020 D5 adds «О бизнесе» here. */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, Building2, Search as SearchIcon } from "lucide-react";
import { Link } from "react-router-dom";

import { Knowledge, Me, Methods } from "@/api/resources";
import type { MethodOut, MethodStatus } from "@/api/types";
import { MethodStatusBadge } from "@/components/MethodBadges";
import { SuggestionCard } from "@/components/SuggestionCard";
import { FilterChip } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { SoloCell } from "@/components/ui/Cell";
import { EmptyState } from "@/components/ui/EmptyState";
import { ScreenTitle, Section } from "@/components/ui/Section";
import { SkeletonList } from "@/components/ui/Skeleton";
import { nameOf, usePeople } from "@/lib/people";

const STATUS_FILTERS: { value: MethodStatus | "all"; label: string }[] = [
  { value: "all", label: "все" },
  { value: "draft", label: "черновики" },
  { value: "proven", label: "проверенные" },
  { value: "needs_review", label: "на пересмотр" },
  { value: "archived", label: "архив" },
];

export function KnowledgeView() {
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [status, setStatus] = useState<MethodStatus | "all">("all");
  const [tag, setTag] = useState<string | null>(null);
  const [author, setAuthor] = useState<string | null>(null);

  const me = useQuery({ queryKey: ["me"], queryFn: Me.get });
  const people = usePeople();

  const methods = useQuery({
    queryKey: ["methods", status, tag],
    queryFn: () =>
      Methods.list({
        status: status === "all" ? undefined : status,
        tag: tag ?? undefined,
        limit: 100,
      }),
  });

  /**
   * Search is the unified one (M2), not `Methods.list({q})`.
   *
   * The difference matters to the person typing: `q` on the list is a
   * substring match over titles, so a miss means "you spelled it differently".
   * This one matches paraphrases across every indexed type, so a miss is a real
   * "we know of nothing like this" — and that is an answer worth trusting.
   */
  const results = useQuery({
    queryKey: ["knowledge-search", submitted],
    queryFn: () => Knowledge.search(submitted, { limit: 20 }),
    enabled: submitted.length > 0,
  });

  const allTags = [...new Set((methods.data ?? []).flatMap((m) => m.tags))].sort();

  const visible = (methods.data ?? []).filter(
    (m) => !author || m.author_id === author,
  );

  const searching = submitted.length > 0;

  return (
    <div className="space-y-4 pt-2">
      <ScreenTitle eyebrow="Как мы это делаем вообще">Знания</ScreenTitle>

      <SoloCell
        to="/business"
        icon={<Building2 className="h-5 w-5" aria-hidden="true" />}
        title="О бизнесе"
        subtitle="Паспорт бизнеса, ресурсы и доступы"
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(query.trim());
        }}
        className="flex gap-2"
      >
        <div className="flex min-h-[46px] flex-1 items-center gap-2 rounded-compact border border-p-border-strong bg-p-surface px-3 focus-within:outline focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-p-focus">
          <SearchIcon className="h-[18px] w-[18px] shrink-0 text-p-text-muted" aria-hidden="true" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Как мы делали похожее?"
            aria-label="Поиск по знаниям"
            className="min-w-0 flex-1 bg-transparent text-[16px] text-p-text outline-none placeholder:text-p-text-muted"
          />
        </div>
        {searching && (
          <Button
            variant="secondary"
            onClick={() => {
              setQuery("");
              setSubmitted("");
            }}
          >
            Сброс
          </Button>
        )}
      </form>

      {searching ? (
        <Section title="Результаты">
          {results.isLoading && <SkeletonList count={3} />}
          {results.isError && (
            <Card className="space-y-2">
              <p className="text-cell text-text">Поиск не отработал.</p>
              <Button size="compact" variant="secondary" onClick={() => void results.refetch()}>
                Повторить
              </Button>
            </Card>
          )}
          {results.data?.length === 0 && (
            <EmptyState
              icon={<SearchIcon className="h-8 w-8" aria-hidden="true" />}
              title="Ничего похожего не нашлось"
              description="Попробуй другие слова или более короткий запрос. Новые материалы могут появиться после обновления поиска."
            />
          )}
          {(results.data ?? []).map((hit) => (
            <SuggestionCard key={`${hit.entity_type}-${hit.entity_id}`} hit={hit} />
          ))}
        </Section>
      ) : (
        <>
          <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
            {STATUS_FILTERS.map(({ value, label }) => (
              <FilterChip key={value} active={status === value} onClick={() => setStatus(value)}>
                {label}
              </FilterChip>
            ))}
          </div>

          {allTags.length > 0 && (
            <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
              <FilterChip active={tag === null} onClick={() => setTag(null)}>
                все теги
              </FilterChip>
              {allTags.map((t) => (
                <FilterChip key={t} active={tag === t} onClick={() => setTag(t)}>
                  {t}
                </FilterChip>
              ))}
            </div>
          )}

          {me.data && (
            <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
              <FilterChip active={author === null} onClick={() => setAuthor(null)}>
                все авторы
              </FilterChip>
              <FilterChip
                active={author === me.data.id}
                onClick={() => setAuthor(author === me.data!.id ? null : me.data!.id)}
              >
                мои
              </FilterChip>
            </div>
          )}

          <Section>
            {methods.isLoading && <SkeletonList count={4} />}
            {methods.isError && (
              <Card className="space-y-2">
                <p className="text-cell text-text">Не получилось загрузить методы.</p>
                <Button size="compact" variant="secondary" onClick={() => void methods.refetch()}>
                  Повторить
                </Button>
              </Card>
            )}
            {!methods.isLoading && !methods.isError && visible.length === 0 && (
              <EmptyState
                icon={<BookOpen className="h-8 w-8" aria-hidden="true" />}
                title="Методов пока нет"
                description="Запиши алгоритм через быстрый ввод или сохрани удачное решение из журнала задачи."
              />
            )}
            {visible.map((method) => (
              <MethodListCard
                key={method.id}
                method={method}
                authorName={nameOf(people.data, method.author_id)}
              />
            ))}
          </Section>
        </>
      )}
    </div>
  );
}

function MethodListCard({
  method,
  authorName,
}: {
  method: MethodOut;
  authorName: string | null;
}) {
  // The list payload carries no application counts, so this card does not
  // claim any. "применён 0×" on a method that has been applied four times is
  // worse than saying nothing — the track record shows on the detail screen.
  return (
    <Link
      to={`/knowledge/${method.id}`}
      className="press-surface flex flex-col gap-1.5 rounded-card bg-sbg p-3"
    >
      <div className="flex items-start gap-2">
        <p className="min-w-0 flex-1 text-cell-title font-medium text-text">{method.title}</p>
        <MethodStatusBadge status={method.status} />
      </div>
      {method.summary && <p className="text-meta text-hint">{method.summary}</p>}
      {method.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {method.tags.map((t) => (
            <span
              key={t}
              className="inline-flex h-5 items-center rounded-badge border border-sep px-1.5 text-micro text-hint"
            >
              {t}
            </span>
          ))}
        </div>
      )}
      {authorName && <p className="text-meta text-hint">{authorName}</p>}
    </Link>
  );
}
